import React from "react";
import { Clock, X, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PollCreatorService } from "../../services/PollCreatorService";

// Types
interface TimeSlot {
  hour: number;
  minute: number;
  label: string;
  enabled: boolean;
}

export interface GranularitySelectorProps {
  timeGranularity: number;
  timeSlots: TimeSlot[];
  timeSlotsByDate: Record<string, TimeSlot[]>;
  showGranularitySettings: boolean;
  onGranularityChange: (value: number) => void;
  onToggleSettings: () => void;
  onUndoChange: () => void;
  className?: string;
}

export const GranularitySelector: React.FC<GranularitySelectorProps> = ({
  timeGranularity,
  timeSlots,
  timeSlotsByDate,
  showGranularitySettings,
  onGranularityChange,
  onToggleSettings,
  onUndoChange,
  className = "",
}) => {
  const { t } = useTranslation("polls");
  const granularityOptions = [
    { value: 15, label: "15 min" },
    { value: 30, label: "30 min" },
    { value: 60, label: t("granularity.option.hour", { count: 1 }) },
    { value: 120, label: t("granularity.option.hours", { count: 2 }) },
    { value: 240, label: t("granularity.option.hours", { count: 4 }) },
  ];

  // Check if there are active time slots selected
  const hasActiveSlots = (() => {
    if (!timeSlotsByDate) return false;

    const selectedSlots = Object.values(timeSlotsByDate).flat();
    const enabledSlots = selectedSlots.filter((slot) => slot.enabled);

    return enabledSlots.length > 0;
  })();

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={onToggleSettings}
        className={`flex items-center gap-2 px-3 py-2 text-sm sm:px-4 sm:text-base border border-border rounded-lg hover:border-primary/50 transition-colors whitespace-nowrap ${
          showGranularitySettings ? "border-primary" : ""
        }`}
      >
        <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
        <span>{t("granularity.button")}</span>
        <ChevronDown
          className={`w-4 h-4 transition-transform ${
            showGranularitySettings ? "rotate-180" : ""
          }`}
        />
      </button>

      {showGranularitySettings && (
        <>
          {/* Backdrop mobile : ferme au tap, absent en desktop (pas de recouvrement). */}
          <div
            className="sm:hidden fixed inset-0 z-[65]"
            onClick={onToggleSettings}
            data-testid="granularity-mobile-backdrop"
          />
          <div
            // Mobile (< sm) : ancrage au bouton overflowait hors écran sur
            // viewport étroit (ex 375px) — le bouton n'est pas forcément à
            // gauche de son wrapper, donc `left-0` + largeur fixe dépassait
            // le bord droit. Fixe et clampé à la viewport (z-[70] > tabbar
            // mobile z-[60], cf. IntentBarMobile) à la place. Desktop (sm+) :
            // dropdown ancré inchangé.
            className="fixed left-4 right-4 top-1/2 -translate-y-1/2 z-[70] sm:absolute sm:top-full sm:left-0 sm:right-auto sm:mt-2 sm:translate-y-0 w-auto sm:w-[min(20rem,calc(100vw-2rem))] bg-card border border-border rounded-lg shadow-lg p-4"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-foreground">
                {t("granularity.title")}
              </h3>
              <button
                onClick={() => {
                  // Fermer les paramètres
                  onToggleSettings();
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {hasActiveSlots && (
              <div className="mb-4 p-3 bg-warning/20 border border-warning/50 rounded-lg">
                <p className="text-sm text-warning">
                  {t("granularity.warning")}
                </p>
              </div>
            )}

            <div className="flex gap-2 flex-wrap">
              {granularityOptions.map((option) => {
                const compatible = (() => {
                  // TOUJOURS autoriser le changement de granularité
                  return true;
                })();
                return (
                  <button
                    key={option.value}
                    onClick={() =>
                      compatible && onGranularityChange(option.value)
                    }
                    disabled={!compatible}
                    className={`px-3 py-1 text-sm rounded-full transition-colors
                      ${
                        timeGranularity === option.value
                          ? "bg-primary text-primary-foreground"
                          : compatible
                            ? "bg-card border border-border hover:border-primary text-foreground"
                            : "bg-background border border-border text-muted-foreground cursor-not-allowed"
                      }
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            {PollCreatorService.initialGranularityState && (
              <button
                onClick={() => {
                  // TODO: Implémenter undoGranularityChange avec setState correct
                  onUndoChange();
                }}
                className="mt-3 text-sm text-muted-foreground hover:text-foreground"
              >
                {t("granularity.undo")}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};
